import { QuestionModel } from "../models/QuestionModel";
import { QuestionType } from "../models/enums/QuestionType";
import { RowType } from "../models/enums/RowType";
import { translate } from "../models/utilities/translationUtilities";
import { isNullOrEmpty } from "../utils";

export function useAriaLabelledByForQuestionOpenEndChoiceBox(question: QuestionModel) {
    switch (question.type) {
        case QuestionType.RadioButton:
        case QuestionType.CheckBox:
        case QuestionType.DrillDown:
        case QuestionType.DropDownList:
        case QuestionType.RespondentLanguage:
        case QuestionType.RespondentTimeZone:
        case QuestionType.TextAnswer:
        case QuestionType.EmailAddressAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.RespondentPhone:
        case QuestionType.OpenEndAnswer:
        // Grid question row/column too have openend inside cell
        // eslint-disable-next-line no-fallthrough
        case QuestionType.ChoiceGrid:
        case QuestionType.ImageChoiceGrid:
        case QuestionType.RunningTotal:
        case QuestionType.StarRatingGrid:
        case QuestionType.NetPromoter:
        case QuestionType.HybridGrid:
            return true;
        default:
            return false;
    }
}

export function useAriaLabelledByForInputFieldQuestion(question: QuestionModel) {
    const questionType = question?.parent?.type ?? question?.type;

    switch (questionType) {
        case QuestionType.TextAnswer:
        case QuestionType.EmailAddressAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.RespondentEmail:
        case QuestionType.RespondentPhone:
        case QuestionType.OpenEndAnswer:
        case QuestionType.ChoiceGrid:
        case QuestionType.ImageChoiceGrid:
        case QuestionType.HybridGrid: // For handling radio/checkbox/image choice rendering
            return true;
        default:
            return false;
    }
}

export function useAriaLabelByForGridRenderedInputFieldQuestion(question: QuestionModel) {
    const questionType = question?.parent?.type ?? question?.type;

    // Donot add Hybrid grid case here as it will break radio/checkbox/image choice rendering
    switch (questionType) {
        case QuestionType.RunningTotal:
            return false;
        default:
            return true;
    }
}

export function isFieldsetGroupingQuestion(question: QuestionModel) {
    switch (question.type) {
        case QuestionType.CheckBox:
        case QuestionType.RadioButton:
        case QuestionType.DrillDown:
            return true;
        default:
            return false;
    }
}

/**
 * Used for AutoComplete combobox only: combines TITLE + LABEL ids into one accessible name
 * so NVDA does not announce the heading and the field as two separate full-question passes.
 */
export function getStandaloneQuestionAriaLabelledBy(
    questionElementId: string,
    hasTitle: boolean,
    hasTextContent: boolean,
): string {
    const ids: string[] = [];
    if (hasTitle) {
        ids.push(`${questionElementId}_TITLE`);
    }
    const includeLabelRegion = !hasTitle || hasTextContent;
    if (includeLabelRegion) {
        ids.push(`${questionElementId}_LABEL`);
    }
    return ids.join(" ");
}

/**
 * Ids for row/column chrome that exist in the grid (see GridRenderer GetRowLeftLabelAriaId / Title HEADER).
 * Omit HEADER when there is only one column — that node is often absent and breaks NVDA if referenced.
 */
export function getStarRatingGridCellAriaLabelledByIds(
    pageQuestion: QuestionModel,
    language: string,
    gridRowIndex0: number,
    gridColIndex0: number,
): string[] {
    const ids: string[] = [];
    if (!pageQuestion?.name) {
        return ids;
    }
    const base = `Q_${pageQuestion.name}`;
    const rowAxis = pageQuestion.rows?.find((r) => r.index === gridRowIndex0);
    const hasRowLabelText =
        rowAxis &&
        (!isNullOrEmpty(translate(rowAxis, "LeftLabel", language)) ||
            !isNullOrEmpty(translate(rowAxis, "TEXT", language)));
    if (hasRowLabelText) {
        ids.push(`${base}_ROW_${gridRowIndex0 + 1}_LEFTLABEL`);
    }
    const colCount = pageQuestion.columns?.length ?? 0;
    if (colCount > 1) {
        ids.push(`${base}_HEADER_${gridColIndex0 + 1}`);
    }
    return ids;
}

/**
 * Plain text from translated strings; translations may contain HTML and entities.
 * Used for aria-label so NVDA reads a short name without re-reading visible _TITLE/_LABEL DOM.
 *
 * Uses the HTML parser (innerHTML → textContent) so decoding matches the spec: named and numeric
 * entities are handled, and user-escaped text like `&amp;lt;` stays the characters `&lt;`, not `<`.
 * When no DOM is available, falls back to stripping tags only (entities left as in the source).
 */
export function stripHtmlForAccessibleName(value: string): string {
    if (isNullOrEmpty(value)) {
        return "";
    }
    if (typeof document !== "undefined") {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = value;
        const plain = wrapper.textContent ?? "";
        return plain.replace(/\s+/g, " ").trim();
    }
    return value
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Plain accessible name for a hybrid grid cell, mirroring GridRenderer header rows (Title + optional Text).
 * Use when interactive controls cannot rely on aria-labelledby → table headers (e.g. table role="presentation"
 * may prune referenced ids). Matches {@link GridRenderer} {@code Labels_*} translation keys.
 *
 * **Text row sub-label index:** {@link GridRenderer} {@code Text} uses {@code Labels_<col>_<n>} where {@code n}
 * runs per choice only when {@code isSplitColumn(column)} (checkbox / radio / image columns). DropDown, Slider,
 * and StarRating columns are never split; they render via {@code VariableCell} with a single Text header cell
 * ({@code index={0}} → {@code Labels_<col>_1}). Plain labels for those controls therefore always use {@code _1}.
 */
export function getHybridGridCellPlainAriaLabel(
    cellQuestion: QuestionModel,
    language: string,
    gridColumnIndex0: number,
    gridRowIndex0: number,
    includeSubHeaderChrome: boolean,
): string {
    const grid = cellQuestion.parent;
    if (
        !grid ||
        grid.type !== QuestionType.HybridGrid ||
        isNullOrEmpty(language) ||
        !grid.rows?.length
    ) {
        return "";
    }

    const parts: string[] = [];

    const variableRow = grid.rows.find(
        (r) => r.type === RowType.Variable && r.index === gridRowIndex0,
    );
    if (variableRow) {
        const left = stripHtmlForAccessibleName(
            translate(variableRow, "LeftLabel", language) || "",
        );
        const text = stripHtmlForAccessibleName(translate(variableRow, "TEXT", language) || "");
        const rowPart = left || text;
        if (rowPart) {
            parts.push(rowPart);
        }
    }

    const titleRow = grid.rows.find((r) => r.type === RowType.Title);
    if (titleRow) {
        const colTitle = stripHtmlForAccessibleName(
            translate(titleRow, `Labels_${gridColumnIndex0 + 1}`, language) || "",
        );
        if (colTitle) {
            parts.push(colTitle);
        }
    }

    if (includeSubHeaderChrome) {
        const textRow = grid.rows.find((r) => r.type === RowType.Text);
        if (textRow) {
            // Sub-label _1 matches GridRenderer Text with index 0 (non-split columns only for VariableCell controls).
            const sub = stripHtmlForAccessibleName(
                translate(textRow, `Labels_${gridColumnIndex0 + 1}_1`, language) || "",
            );
            if (sub) {
                parts.push(sub);
            }
        }
    }

    return parts.join(", ");
}

/**
 * Ids in QuestionRenderer (TITLE and/or _LABEL) so a single focus target can reference
 */
export function getQuestionTitleAndLabelAriaLabelledByIds(
    question: QuestionModel,
    language: string,
): string {
    const title = translate(question, "TITLE", language);
    const text = translate(question, "TEXT", language);
    const shortText = translate(question, "SHORT_TEXT", language);
    const hasTitle = !isNullOrEmpty(title);
    const hasTextContent = !isNullOrEmpty(text) || !isNullOrEmpty(shortText);
    const idBase = question?.elementId;
    if (!idBase) {
        return "";
    }
    const ids: string[] = [];
    if (hasTitle) {
        ids.push(`${idBase}_TITLE`);
    }
    if (!hasTitle || hasTextContent) {
        ids.push(`${idBase}_LABEL`);
    }
    return ids.join(" ");
}

/**
 * Standalone-only Title+Label accessible name for the six top-level text-family input types
 * (DEV-98353). Self-guards on an explicit type whitelist plus the absence of a parent question,
 * so it is safe to call from renderers shared with grid cells and choice other-specify boxes
 * (RadioButton/CheckBox other-specify open-ends have `question.parent` set and a non-grid
 * `gridRendering`, so a `!gridRendering` check alone is not sufficient — see solution doc).
 * Returns "" when not applicable; callers fall back to their existing chrome-id logic.
 */
export function getStandaloneInputAriaLabelledBy(
    question: QuestionModel,
    language: string,
): string {
    const standaloneInputTypes = [
        QuestionType.TextAnswer,
        QuestionType.EmailAddressAnswer,
        QuestionType.NumericAnswer,
        QuestionType.OpenEndAnswer,
        QuestionType.RespondentEmail,
        QuestionType.RespondentPhone,
    ];
    if (
        !question ||
        question.parent ||
        !language ||
        !standaloneInputTypes.includes(question.type)
    ) {
        return "";
    }
    return getQuestionTitleAndLabelAriaLabelledByIds(question, language);
}

/**
 * Prepends parent HybridGrid TITLE/LABEL ids for the survey's first (lead) hybrid variable cell only.
 * Bridges / role="group" wrappers use this; inner controls keep cell chrome only so refocusing does not
 * re-read the full question (same pattern as StarRatingGrid / star radiogroup).
 */
export function hybridGridLeadCellGroupAriaLabelledByIds(
    gridQuestion: QuestionModel,
    language: string | undefined,
    isLeadVariableCell: boolean,
    cellChromeLabelIds: string,
): string {
    if (!isLeadVariableCell || !language || gridQuestion.type !== QuestionType.HybridGrid) {
        return cellChromeLabelIds;
    }
    const titleIds = getQuestionTitleAndLabelAriaLabelledByIds(gridQuestion, language);
    if (!titleIds) {
        return cellChromeLabelIds;
    }
    return `${titleIds} ${cellChromeLabelIds}`.trim();
}

export function getAriaLabelledByFromId(
    question: QuestionModel,
    nameOrElementId: string,
    fieldWithinQuestionType?: string,
    gridRenderedColumnIndex?: number,
    gridRenderedRowIndex?: number,
    gridRenderedChoiceIndex?: number,
    language?: string,
    isFirstGridInputForParentQuestion?: boolean,
    /** When true (second header row present), hybrid grid INPUTBOX/slider/star ids include SUBHEADER chrome. */
    includeHybridGridSubHeaderChrome?: boolean,
) {
    if (!nameOrElementId) {
        return "";
    }
    let questionType: any;

    if (fieldWithinQuestionType) {
        questionType = fieldWithinQuestionType;
    } else {
        questionType = question?.parent?.type ?? question?.type;
    }

    // Q_<survey name> prefix for HybridGrid column/chrome ids when they must match GridCellRenderer
    // despite a different matrix elementId passed as nameOrElementId.
    const gridQuestionIdPrefix = question?.parent?.name ?? question?.name;
    const gridQuestionId = gridQuestionIdPrefix
        ? gridQuestionIdPrefix.startsWith("Q_")
            ? gridQuestionIdPrefix
            : `Q_${gridQuestionIdPrefix}`
        : nameOrElementId;
    const hybridGridLeftLabelId = `${gridQuestionId}_ROW_${gridRenderedRowIndex}_LEFTLABEL`;
    const legacyLeftLabelId = `${nameOrElementId}_ROW_${gridRenderedRowIndex}_LEFTLABEL`;

    // If same question have 2 field types use fieldWithinQuestionType case first
    switch (questionType) {
        case QuestionType.RadioButton:
        case QuestionType.CheckBox:
        case QuestionType.TextAnswer:
        case QuestionType.EmailAddressAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.RespondentEmail:
        case QuestionType.RespondentPhone:
        case QuestionType.OpenEndAnswer:
        case QuestionType.NetPromoter:
            return `${nameOrElementId}_LABEL`;
        case `${QuestionType.RunningTotal}_INPUTBOX`: {
            const columnHeaderId = `${gridQuestionId}_HEADER_${gridRenderedColumnIndex}`;
            // First *visible* variable cell (row-major, respects shouldHideCell), not "column index 1".
            // If column 1 is hidden, the first focusable input may be column 2+ and must still get TITLE/LABEL.
            if (isFirstGridInputForParentQuestion !== true) {
                return `${legacyLeftLabelId} ${columnHeaderId}`.trim();
            }
            if (language && question?.parent?.type === QuestionType.RunningTotal) {
                const titleLabelIds = getQuestionTitleAndLabelAriaLabelledByIds(
                    question.parent,
                    language,
                );
                if (titleLabelIds) {
                    return `${titleLabelIds} ${legacyLeftLabelId} ${columnHeaderId}`.trim();
                }
            }
            return `${nameOrElementId}_TITLE ${nameOrElementId}_LABEL ${legacyLeftLabelId} ${columnHeaderId}`;
        }
        case `${QuestionType.ChoiceGrid}_CHOICEOPTION`:
        case `${QuestionType.ImageChoiceGrid}_CHOICEOPTION`: {
            const gridParentQuestion = question?.parent ?? question;
            const questionTitleAndLabelIds =
                isFirstGridInputForParentQuestion && language
                    ? getQuestionTitleAndLabelAriaLabelledByIds(gridParentQuestion, language)
                    : "";
            return `${questionTitleAndLabelIds} ${legacyLeftLabelId} ${nameOrElementId}_CHOICEHEADER_${gridRenderedChoiceIndex}`.trim();
        }
        case QuestionType.ChoiceGrid:
        case QuestionType.ImageChoiceGrid:
        case QuestionType.RunningTotal:
        case QuestionType.StarRatingGrid:
            return `${nameOrElementId}_CELL_LABEL`;
        case `${QuestionType.HybridGrid}_OPENENDROWHEADER`:
            return `${hybridGridLeftLabelId}`;
        case `${QuestionType.ChoiceGrid}_OPENENDROWHEADER`:
        case `${QuestionType.ImageChoiceGrid}_OPENENDROWHEADER`:
        case `${QuestionType.RunningTotal}_OPENENDROWHEADER`:
        case `${QuestionType.StarRatingGrid}_OPENENDROWHEADER`:
            return `${legacyLeftLabelId}`;
        case QuestionType.DrillDown:
        case QuestionType.DropDownList:
        case QuestionType.RespondentLanguage:
        case QuestionType.RespondentTimeZone:
            return nameOrElementId;
        case `${QuestionType.HybridGrid}_INPUTBOX`: {
            // Column HEADER ids use Q_<question.name> (see GridRenderer Title); SUBHEADER from RowType.Text row.
            const headerParts = [
                hybridGridLeftLabelId,
                `${gridQuestionId}_HEADER_${gridRenderedColumnIndex}`,
            ];
            if (includeHybridGridSubHeaderChrome) {
                headerParts.push(`${gridQuestionId}_SUBHEADER_${gridRenderedColumnIndex}`);
            }
            return headerParts.filter(Boolean).join(" ");
        }
        // Bridge carries row/column; each inner input uses main-column choice header only.
        case `${QuestionType.HybridGrid}_CHOICEOPTION_RADIO_SCOPED`:
        case `${QuestionType.HybridGrid}_CHOICEOPTION_INPUT_SCOPED`:
        case `${QuestionType.HybridGrid}_CHOICEOPTION_IMAGE_SCOPED`:
            return `${gridQuestionId}_MAINCOLHEADER${gridRenderedColumnIndex}_CHOICEHEADER_${gridRenderedChoiceIndex}`;
        /** Hybrid grid image column: same chrome ids as CHOICEOPTION (row + column + per-choice sub-header); see GridCellRenderer MAINCOLHEADER/CHOICEHEADER. */
        case `${QuestionType.HybridGrid}_CHOICEOPTION_IMAGE`:
            return `${hybridGridLeftLabelId} ${gridQuestionId}_HEADER_${gridRenderedColumnIndex} ${gridQuestionId}_MAINCOLHEADER${gridRenderedColumnIndex}_CHOICEHEADER_${gridRenderedChoiceIndex}`;
        case `${QuestionType.HybridGrid}_CHOICEOPTION`:
            // MAINCOLHEADER/HEADER chrome ids match GridCellRenderer (Q_<question.name>_…), not nameOrElementId.
            return `${hybridGridLeftLabelId} ${gridQuestionId}_HEADER_${gridRenderedColumnIndex} ${gridQuestionId}_MAINCOLHEADER${gridRenderedColumnIndex}_CHOICEHEADER_${gridRenderedChoiceIndex}`;
        case `${QuestionType.HybridGrid}_SLIDER`:
        case `${QuestionType.HybridGrid}_STARRATING`: {
            // Same pattern as HybridGrid_INPUTBOX: row + column HEADER (+ optional SUBHEADER).
            const starSliderParts = [
                hybridGridLeftLabelId,
                `${gridQuestionId}_HEADER_${gridRenderedColumnIndex}`,
            ];
            if (includeHybridGridSubHeaderChrome) {
                starSliderParts.push(`${gridQuestionId}_SUBHEADER_${gridRenderedColumnIndex}`);
            }
            return starSliderParts.filter(Boolean).join(" ");
        }
        default:
            return "";
    }
}

export function getRoleForQuestion(question: QuestionModel) {
    switch (question.type) {
        case QuestionType.RadioButton:
        case QuestionType.CheckBox:
            return undefined;
        // For below question types normal a text box is display but choices can also be displayed
        // and because it doesn't have any grouping i.e. radiogroup is used
        case QuestionType.EmailAddressAnswer:
        case QuestionType.TextAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.OpenEndAnswer:
            return "radiogroup";
        default:
            return "group";
    }
}

export function getIdForAriaLabelledFieldBySection(condition: string) {
    switch (condition) {
        case "NAVLANGSELECTOR":
            return "SURVEYNAVIGATIONLANGUAGESELECTOR";
        default:
            return condition;
    }
}
